<template>
  <div class="h-full overflow-scroll">
    <b-loading :active="loading" />
    <div class="temp-report">
      <a class="mr-auto" href="/">
        <img
          class="sm:h-6"
          src="@/assets/images/logo-blue.svg"
          alt="logo" />
      </a>
      <div class="flex flex-grow justify-center mt-[16px] relative sm:mt-10">
        <div class="report-block">
          <div v-if="!isExpired && !loading">
            <h2 class="font-medium mb-2 text-black text-[32px]">{{ report.name }}</h2>
            <span :class="statusTagClass" class="border px-2 py-1 rounded-full text-sm">
              {{ statusText }}
            </span>
            <div class="flex justify-between mb-6 mt-2">
              <div class="mt-4">
                <div class="flex flex-col gap-2">
                  <span class="text-gray-500">開放訂購時間</span>
                  <w-date-time-display
                    v-if="report?.start_time"
                    :date-time="report"
                    text-bold
                    text-size-class="text-base"
                    time-text-class="font-medium text-black"
                  />
                </div>
                <div class="flex flex-col gap-2 mt-6">
                  <span class="text-gray-500">領取時間</span>
                  <div class="flex gap-4 items-center">
                    <w-date-time-display
                      v-if="report?.receive_times?.length"
                      :date-time="report?.receive_times?.[0]"
                      text-bold
                      text-size-class="text-base"
                      time-text-class="font-medium text-black"
                    />
                    <b-dropdown
                      v-if="report?.receive_times?.length > 1"
                      :mobile-modal="false"
                      append-to-body
                      aria-role="list"
                      max-height="200"
                      position="is-bottom-left"
                      scrollable
                    >
                      <template #trigger>
                        <i class="cursor-pointer icon-down-chevron text-base" />
                      </template>
                      <b-dropdown-item
                        v-for="(receiveTime, index) in extraReceiveTimes(report.receive_times)"
                        :key="`receiveTime-${index}`"
                        :focusable="false"
                        aria-role="listitem"
                        class="w-max"
                        custom
                      >
                        <w-date-time-display
                          :date-time="receiveTime"
                          time-text-class="text-black" />
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
              </div>
              <img
                class="sm:hidden"
                src="@/assets/images/affiliate.svg"
                alt="affiliate" />
            </div>
            <span v-if="report?.calculate_time" class="text-black text-sm">
              報表統計時間：{{ calculateTime }}
            </span>
            <div class="detail-card detail-card--info my-6">
              <div class="flex">
                <div class="border-blue-100 border-r flex flex-col gap-1 pr-4">
                  <span class="text-gray-500">預估品項總數量</span>
                  <span class="font-medium text-blue-500 text-2xl">
                    {{ (report?.quantity || 0) | commaFormat }}
                  </span>
                </div>
                <div class="flex flex-col gap-1 pl-4">
                  <span class="text-gray-500">預估總金額</span>
                  <span class="font-medium text-blue-500 text-2xl">
                    $ {{ (report?.total || 0) | commaFormat }}
                  </span>
                </div>
              </div>
              <b-table
                :data="orderItems"
                :height="$breakpoints.sm ? 560 : 'auto'"
                :mobile-cards="false"
                class="mt-4"
                sticky-header>
                <b-table-column
                  header-class="text-sm min-w-[200px] px-4 py-3"
                  cell-class="max-w-[812px] min-w-[200px] px-4 py-3"
                  field="name"
                  label="品項名稱"
                  v-slot="props"
                >
                  {{ props.row.entree.name }}
                </b-table-column>
                <b-table-column
                  header-class="text-sm px-4 py-3"
                  cell-class="px-4 py-3"
                  field="quantity"
                  label="預估數量"
                  centered
                  v-slot="props"
                  width="80"
                >
                  {{ props.row.quantity }}
                </b-table-column>
                <b-table-column
                  header-class="text-sm px-4 py-3"
                  cell-class="px-4 py-3"
                  field="price"
                  label="單價"
                  numeric
                  v-slot="props"
                  width="80"
                >
                  {{ props.row.price | priceFormat }}
                </b-table-column>
                <b-table-column
                  header-class="text-sm px-4 py-3"
                  cell-class="px-4 py-3"
                  field="subtotal"
                  label="預估小計"
                  numeric
                  v-slot="props"
                  width="80"
                >
                  {{ props.row.subtotal | priceFormat }}
                </b-table-column>
                <b-table-column
                  header-class="text-sm px-4 py-3"
                  cell-class="px-4 py-3"
                  field="note"
                  label="備註"
                  v-slot="props"
                  width="96"
                >
                  {{ props.row.note }}
                </b-table-column>
                <template #empty>
                  <div class="text-center py-3">無訂購資料</div>
                </template>
              </b-table>
            </div>
            <span class="text-gray-500 text-xs">
              統計資料為即時數據，於訂購結束前仍會持續更新。若欲獲得最終統計數據，請於訂購結束後重新查詢。此報表連結將於訂購結束後30日自動失效。
            </span>
          </div>
          <div v-else-if="!loading" class="flex justify-center mt-[164px]">
            <div class="flex flex-col gap-14 items-center">
              <h2 class="font-medium mb-2 text-black text-[32px]">連結已失效</h2>
              <img
                class="sm:w-[260px]"
                src="@/assets/images/expired.svg"
                alt="logo" />
            </div>
          </div>
          <div></div>
          <p class="mt-6 text-gray-500 text-center text-sm sm:px-8">
            Copyright © {{ currentYear }} 微碧愛普科技有限公司. 保留一切權利。
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TempReport from './TempReport';

export default TempReport;
</script>

<style lang="scss" scoped>
@import 'TempReport';
</style>
